// src/components/PageSkeleton.tsx
//
// Route-level loading states.
//
// These stand in for a page while its chunk and data load. They mirror the
// real layout — header block, then the page's dominant shape — so the
// content lands in place instead of the page reflowing once it arrives.
// That is the whole point: a centred spinner tells you nothing and then
// jumps.

import { Skeleton } from "@/components/ui/skeleton";

/** Matches the shell's content container so skeleton and real page share
 *  the same gutters at every breakpoint. */
export function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 py-6 sm:px-6 sm:py-8">
      {children}
    </div>
  );
}

/** Page title + subtitle, present on nearly every screen. */
function HeaderSkeleton() {
  return (
    <div className="flex flex-col gap-2.5">
      <Skeleton className="h-7 w-56 sm:h-8 sm:w-72" />
      <Skeleton className="h-4 w-full max-w-md" />
    </div>
  );
}

/** Rows-over-cards is the design's default for lists (papers, sessions,
 *  subjects), so this is the most reused skeleton in the app. */
export function RowListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="border-b border-border bg-subtle px-4 py-2.5 sm:px-5">
        <Skeleton className="h-3 w-32" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-border/60 px-4 py-3.5 last:border-b-0 sm:gap-4 sm:px-5"
          // Each row starts its sweep slightly later, so the list reads
          // top-to-bottom rather than flashing as one block.
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <Skeleton className="h-4 w-10 shrink-0" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="hidden h-4 w-20 sm:block" />
          <Skeleton className="h-7 w-16 shrink-0 rounded-lg sm:w-24" />
        </div>
      ))}
    </div>
  );
}

export function CardGridSkeleton({ cards = 4 }: { cards?: number }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: cards }).map((_, i) => (
        <div
          key={i}
          className="flex min-h-[130px] flex-col gap-2.5 rounded-xl border border-border bg-card p-4"
        >
          <Skeleton className="h-7 w-7 rounded-lg" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

/** Generic list page: header, filter strip, rows. */
export function ListPageSkeleton() {
  return (
    <PageShell>
      <div className="flex flex-col gap-5">
        <HeaderSkeleton />
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-9 w-48 rounded-lg" />
          <Skeleton className="h-9 w-32 rounded-lg" />
        </div>
        <RowListSkeleton />
      </div>
    </PageShell>
  );
}

/** Home: hero pair, feature cards, then the two-column body. */
export function HomeSkeleton() {
  return (
    <PageShell>
      <div className="flex flex-col gap-6">
        <HeaderSkeleton />
        <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1.55fr_1fr]">
          <Skeleton className="h-[186px] rounded-xl" />
          <Skeleton className="h-[186px] rounded-xl" />
        </div>
        <CardGridSkeleton />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_336px]">
          <Skeleton className="h-52 rounded-xl" />
          <Skeleton className="h-52 rounded-xl" />
        </div>
      </div>
    </PageShell>
  );
}

/** Two-column detail pages: syllabus, announcements, contests. */
export function SplitPageSkeleton() {
  return (
    <PageShell>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
        <div className="flex flex-col gap-4">
          <HeaderSkeleton />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
        <div className="flex flex-col gap-3.5">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      </div>
    </PageShell>
  );
}

/** Full-screen flows with no nav chrome — onboarding, contest play. */
export function CenteredSkeleton() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4">
      <Skeleton className="h-7 w-72 max-w-full" />
      <Skeleton className="h-4 w-96 max-w-full" />
      <div className="mt-3 grid w-full max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-xl" />
        ))}
      </div>
    </div>
  );
}
